"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SimpleFormulaNode = void 0;
const tree_node_1 = require("./base/tree.node");
const internal_node_1 = require("./base/internal.node");
const sheet_db_1 = require("../../sheet_db");
const formula_node_1 = require("./formula.node");
const math_nodes_1 = require("./math_nodes");
const comparison_node_1 = require("./comparison.node");
const boolean_node_1 = require("./boolean.node");
const symbol_node_1 = require("./symbol.node");
const antlr_files_1 = require("../antlr_files");
const constants_1 = require("../constants");
const array_node_1 = require("./array.node");
const utils_1 = require("../../utils");
const constant_node_1 = require("./constant.node");
const nodeTypesToFilter = new Set([
    tree_node_1.TreeNodeType.COLUMN_NODE,
    tree_node_1.TreeNodeType.FORMULA_NODE,
]);
class SimpleFormulaNode extends internal_node_1.InternalNode {
    _children;
    _formulaName;
    _originalSymbol;
    constructor(originalNode, parent, prefix, metadata) {
        super({ getText: () => '' }, parent);
        const newName = this.getExprFormulaName(originalNode, metadata);
        this.formulaName = prefix ? `${prefix}${newName}` : newName;
        this.replaceOriginalNode(originalNode);
        this.handleChildren(originalNode);
        this.parent = parent;
        this._children = originalNode.children;
    }
    set formulaName(value) {
        this._formulaName = value;
    }
    accept(v) {
        if (v.visitSimpleFormula)
            v.visitSimpleFormula(this);
        this.visitChildren(v);
    }
    getReturnType() {
        return '';
    }
    handleChildren(originalNode) {
        if (!this._originalSymbol || this._originalSymbol.terminalType !== antlr_files_1.Lexer.BOOLEAN_OPERATIONS)
            return;
        const arrayNode = new array_node_1.ArrayNode(originalNode.children);
        arrayNode.parent = this;
        originalNode.children = [arrayNode];
    }
    replaceOriginalNode(originalNode) {
        for (const child of originalNode.children) {
            child.parent = this;
        }
        const parent = originalNode.parent;
        const children = [
            ...(parent instanceof formula_node_1.FormulaNode ? parent.rawChildren : parent?.children ?? []),
        ];
        const nodeIndex = children?.findIndex(child => Object.is(child, originalNode)) ?? -1;
        if (!parent || !children || nodeIndex === -1)
            return;
        children[nodeIndex] = this;
        parent.children = children;
    }
    toString() {
        if (!this.hasChildren())
            return '';
        return `${this._formulaName}(${this._children.map(child => child.toString()).join(', ')})`;
    }
    get treeNodeType() {
        return tree_node_1.TreeNodeType.SIMPLE_FORMULA_NODE;
    }
    getExprFormulaName(node, metadata) {
        let name = '';
        if (!(node instanceof math_nodes_1.PlusMinusExprNode ||
            node instanceof comparison_node_1.ComparisonExprNode ||
            node instanceof math_nodes_1.TimesDivExprNode ||
            node instanceof boolean_node_1.BooleanExprNode ||
            node instanceof math_nodes_1.PowExprNode))
            return name;
        const symbolNode = node.children.find(child => child instanceof symbol_node_1.SymbolNode);
        this._originalSymbol = symbolNode;
        if (!symbolNode)
            return name;
        switch (symbolNode.terminalType) {
            case antlr_files_1.Lexer.BOOLEAN_OPERATIONS:
                name = constants_1.BOOLEAN_OPERATIONS_TO_BQ_FORMULA[symbolNode.token];
                break;
            case antlr_files_1.Lexer.COMPARISON_OPERATIONS:
                name = constants_1.COMPARISON_OPERATIONS_TO_BQ_FORMULA[symbolNode.token];
                if (metadata?.comparisonFormulaMode !== 'strict_type_formulas')
                    break;
                const leftChild = node.children[0];
                const rightChild = node.children[2];
                const leftColumnType = this.recursiveGetReturnType(leftChild, metadata);
                const rightColumnType = this.recursiveGetReturnType(rightChild, metadata);
                // IGNORE SECOND CHILD, the symbol
                const isIndexToIgnore = (index) => index === 1;
                this.normalizeConstantNode(node, isIndexToIgnore, metadata);
                const leftSideMultipleReturnType = leftChild instanceof formula_node_1.FormulaNode && leftChild.isMultipleReturnType();
                const rightSideMultipleReturnType = rightChild instanceof formula_node_1.FormulaNode && rightChild.isMultipleReturnType();
                const multipleReturnType = leftSideMultipleReturnType || rightSideMultipleReturnType;
                const subFormula = this.columnTypeToSubFormula({
                    columnType: leftColumnType == rightColumnType ? leftColumnType : sheet_db_1.ColumnType.STRING,
                    multipleReturnType,
                });
                name += subFormula;
                break;
            default:
                name = constants_1.EXP_SYMBOL_TO_BQ_FORMULA[symbolNode.terminalType];
                break;
        }
        node.children = node.children.filter(child => !(child instanceof symbol_node_1.SymbolNode));
        return name;
    }
    recursiveGetReturnType(node, metadata, isIndexToIgnore) {
        if (node instanceof constant_node_1.ConstantNode) {
            // IF CONSTANT IS STRING, but it's using with a STRING COLUMN or FORMULA, it should be a NUMBER RETURN TYPE
            const nodeType = node.getReturnType();
            const filteredChildren = node.parent?.children.filter((child, index) => !isIndexToIgnore?.(index) && nodeTypesToFilter.has(child.treeNodeType)) ?? [];
            const numberSiblingsType = filteredChildren.every(child => {
                if (child instanceof formula_node_1.FormulaNode && child.isMultipleReturnType()) {
                    return (this.recursiveGetReturnType(child, metadata, isIndexToIgnore) === sheet_db_1.ColumnType.NUMBER);
                }
                if (child.getReturnType() === sheet_db_1.ColumnType.NUMBER)
                    return true;
                return false;
            });
            return filteredChildren.length > 0 && numberSiblingsType === true
                ? sheet_db_1.ColumnType.NUMBER
                : nodeType ?? sheet_db_1.ColumnType.STRING;
        }
        if (!(node instanceof formula_node_1.FormulaNode)) {
            return node.getReturnType() ?? sheet_db_1.ColumnType.STRING;
        }
        if (!node.isMultipleReturnType()) {
            return node.getReturnType() ?? sheet_db_1.ColumnType.STRING;
        }
        if (node.formulaName === 'IF' || node.formulaName === 'IFERROR') {
            const isIndexToIgnore = (index) => index === 0; // IGNORE FIRST
            const secondArg = this.recursiveGetReturnType(node.children[1], metadata, isIndexToIgnore);
            // arg 2 is optional for iferror
            let thirdArg = null;
            if (node.children[2])
                thirdArg = this.recursiveGetReturnType(node.children[2], metadata, isIndexToIgnore);
            this.normalizeConstantNode(node, isIndexToIgnore, metadata);
            return !thirdArg ? secondArg : secondArg == thirdArg ? secondArg : sheet_db_1.ColumnType.STRING;
        }
        if (node.formulaName === 'IFS') {
            const isIndexToIgnore = (index) => index % 2 == 0; // IGNORE EVEN
            const args = node.children.flatMap((child, index) => {
                if (index % 2 == 0)
                    return [];
                return [this.recursiveGetReturnType(child, metadata, isIndexToIgnore)];
            });
            this.normalizeConstantNode(node, isIndexToIgnore, metadata);
            return args.every(arg => arg == args[0]) ? args[0] : sheet_db_1.ColumnType.STRING;
        }
        return sheet_db_1.ColumnType.STRING;
    }
    normalizeConstantNode(node, isIndexToIgnore, metadata) {
        let allTypes = [];
        let allConstantNodes = [];
        // GET ALL CONSTANTS and generate AllTypes
        node.children.forEach((child, index) => {
            if (isIndexToIgnore(index))
                return;
            if (child.treeNodeType === tree_node_1.TreeNodeType.CONSTANT_NODE)
                allConstantNodes.push(child);
            const type = this.recursiveGetReturnType(child, metadata);
            let isPlanVariable = false;
            if (child.treeNodeType === tree_node_1.TreeNodeType.COLUMN_NODE) {
                const columnNode = child;
                isPlanVariable = columnNode.isPlanVariable();
            }
            allTypes.push({ columnType: type, treeNodeType: child.treeNodeType, isPlanVariable });
        });
        // CHECK IF ALL TYPES ARE STRING OR DATE
        // IF STRING and NOT CONSTANT, check if it is a plan variable
        for (const t of allTypes) {
            if (t.columnType !== sheet_db_1.ColumnType.STRING && t.columnType !== sheet_db_1.ColumnType.DATE)
                return;
            if (t.columnType === sheet_db_1.ColumnType.STRING &&
                t.treeNodeType !== tree_node_1.TreeNodeType.CONSTANT_NODE &&
                !t.isPlanVariable)
                return;
        }
        // CHECK IF ALL CONSTANTS ARE VALID DATES
        if (!allConstantNodes.every(nd => {
            const { isValid } = (0, utils_1.validateAndParseDate)(nd.text.slice(1, -1));
            return isValid;
        }))
            return;
        // IF ALL CONDITIONS ARE MET, NORMALIZE CONSTANTS
        node.children.forEach((child, index) => {
            if (isIndexToIgnore(index))
                return;
            if (child.treeNodeType === tree_node_1.TreeNodeType.CONSTANT_NODE &&
                child.getReturnType() === sheet_db_1.ColumnType.STRING) {
                const { parsedString } = (0, utils_1.validateAndParseDate)(child.text.slice(1, -1));
                const newChildren = [...node.children];
                newChildren[index] = this.getNormalizedConstantNode(parsedString, node);
                node.children = newChildren;
            }
        });
    }
    getNormalizedConstantNode(parsedString, node) {
        return new constant_node_1.ConstantNode({
            getText: () => `'${parsedString}'`,
            symbol: { start: 0, stop: 0, type: 0 },
        }, node);
    }
    columnTypeToSubFormula({ columnType, multipleReturnType, }) {
        if (multipleReturnType)
            return '';
        switch (columnType) {
            case sheet_db_1.ColumnType.NUMBER:
                return '_NUMBER';
            default:
                return '_STRING';
        }
    }
}
exports.SimpleFormulaNode = SimpleFormulaNode;
